import { forwardRef, KeyboardEvent, RefObject, useCallback, useImperativeHandle, useState } from 'react';
import { DispatchLeg, updateConsignmentInDispatchCache } from '@api/Trinium/Endpoints/Dispatch/types';
import { sendConsignmentMutationsAsync } from '@api/Trinium/Endpoints/Consignment/actions';
import { getApiErrorMessage } from '@api/getApiError';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { clearLegFeedback, setLegLoading } from '@dispatchBoard/dispatchBoardSlice';
import RenderHeader from '@components/DataGrid/RenderHeader';
import { EditableCellRef, emptyLastSign } from '@components/DataGrid/types';
import TextField from '@forms/Components/TextInput';
import { useAppDispatch, useAppSelector } from '@reduxStore';
import { applyColumnQuickFilter, caseInsensitiveIncludes } from '../columns';
import { EditableCell } from '../types';
import { GridColDefExtended } from '@mui/x-data-grid-pro';

import styles from '../../RenderCell.module.css';

const headerDisplayName = 'Order Notes';

const consignmentNotesDefinition: GridColDefExtended<DispatchLeg> = {
  field: 'Consignment Notes',
  minWidth: 150,
  width: 250,
  headerName: headerDisplayName,
  headerDisplayName,
  display: 'flex',
  renderHeader: () => <RenderHeader primaryText={headerDisplayName} />,
  renderCell: ({ row }) => {
    if (!row.csnNotes) {
      return null;
    }

    /** .notes-preview line-clamps the text; the full note is on the native tooltip, as in every other column. */
    return (
      <div
        className={styles['notes-preview']}
        title={row.csnNotes}>
        {row.csnNotes}
      </div>
    );
  },
  disableReorder: true,
  getApplyQuickFilterFn: (value: string) => {
    return (_, row): boolean =>
      applyColumnQuickFilter(value, row, (value: string, row: DispatchLeg) => caseInsensitiveIncludes(value, [row.csnNotes]));
  },
  valueGetter: (_, row: DispatchLeg) => row.csnNotes ?? '',
  sortable: true,
  sortComparator: (v1, v2, cellParams1) => {
    if (typeof v1 === 'string' && typeof v2 === 'string') {
      /** Empties go last regardless of sort direction */
      if (!v1 && !v2) {
        return 0;
      }
      if (!v1) {
        return emptyLastSign(cellParams1);
      }
      if (!v2) {
        return -emptyLastSign(cellParams1);
      }

      return v1.localeCompare(v2, undefined, { sensitivity: 'base' });
    }

    return 0;
  },
  /** The note belongs to the Consignment, so one save would rewrite it for every leg of that order — edit one row at a time */
  disableBatchEdit: true,
  renderEditableCell: (row: DispatchLeg, ref: RefObject<EditableCellRef | null>) => {
    /** A cancelled or closed load is read-only */
    if (row.isDisabled) {
      return null;
    }

    return (
      <ConsignmentNotesEditCell
        row={row}
        ref={ref}
      />
    );
  }
};

/**
 * Component to render when the Order Notes cell is clicked and enters edit mode
 */
const ConsignmentNotesEditCell = forwardRef<EditableCellRef, EditableCell<DispatchLeg>>(({ row }, ref) => {
  const appDispatch = useAppDispatch();
  const initialNotes = row.csnNotes ?? '';
  const [notes, setNotes] = useState(initialNotes);
  const isLoading = useAppSelector((state) => state.dispatchBoardState.loadingSet.includes(row.legId));

  const saveHandler = useCallback(async () => {
    if (notes === initialNotes) {
      return;
    }

    /** Shown straight away because the note is the dispatcher's own text — the server has nothing to add to it */
    const optimisticUpdates = updateConsignmentInDispatchCache(row, { csnNotes: notes });

    /**
     * The consignment endpoints never touch the loading set — only the Leg ones do — so the row would
     * otherwise sit there with no feedback while the save is in flight.
     */
    appDispatch(setLegLoading(row.legId));

    try {
      /** Re-stamps the note onto every dispatch row of this consignment from the response, in case the server trimmed it */
      await sendConsignmentMutationsAsync(row.csnId, { notes });
    } catch (error: unknown) {
      optimisticUpdates.forEach((optimisticUpdate) => {
        optimisticUpdate.undo();
      });
      appDispatch(appendErrorAnnouncement({ message: getApiErrorMessage(error) }));
    } finally {
      appDispatch(clearLegFeedback(row.legId));
    }
  }, [appDispatch, notes, initialNotes, row]);

  /** Define the callable Ref functions */
  useImperativeHandle(ref, () => {
    return {
      saveChanges: saveHandler
    };
  }, [saveHandler]);

  /** Enter starts a new line in the note, so it must not reach the editor's save-on-Enter handler */
  const swallowEnter = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.stopPropagation();
    }
  };

  return (
    <div
      style={{ width: '320px' }}
      onKeyDown={swallowEnter}>
      {/** Outlined rather than the standard variant the single-line editors use: .standard pins the input to 24px, which clips a textarea */}
      <TextField
        fullWidth
        value={notes}
        onChange={(event) => {
          setNotes(event.target.value);
        }}
        focused
        disabled={isLoading}
        customProps={{
          labelText: 'Order Notes',
          textArea: true,
          rows: 3,
          isDirty: false,
          errorText: undefined,
          onClear: () => {
            setNotes('');
          },
          disabledReason: isLoading ? 'Updating...' : undefined
        }}
      />
    </div>
  );
});

export default consignmentNotesDefinition;
